import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import moment from "moment";

import {
  COPY_PLAN_WITH_DRIVERS,
  DAYS_TO_NUMBER,
} from "../../../consts/strings";

import { copyModalOpenSelector } from "../../../reducers/ownApp/selectors";
import { copyPlan } from "../../../reducers/matrixProfile/thunkActions";
import { activeSaebiVersionSelector } from "../../../reducers/depot/selectors";

import {
  isDailyPlanSelector,
  getSelectedMatrixProfile,
  getMatrixProfiles,
  hasDriverlessRoutesSelector,
} from "../../../reducers/matrixProfile/selectors";
import { setCopyModalOpen } from "../../../reducers/ownApp";

import { findProfile } from "../../../utils/plans";

import { Modal } from "../Modal";
import { CopyContent } from "./CopyContent";
import { CopyActions } from "./CopyActions";
import { ConfirmDialog } from "../../ConfirmDialog";
import { WarningIcon } from "../../Icons";

const START_OF_WEEK_DAYS = [DAYS_TO_NUMBER.Mon, DAYS_TO_NUMBER.Tue];
const WEEKEND_DAYS = [DAYS_TO_NUMBER.Sat, DAYS_TO_NUMBER.Sun];

const peerDaysToBlock = (dayNumbers) => {
  if (dayNumbers.some((day) => START_OF_WEEK_DAYS.includes(day)))
    return WEEKEND_DAYS;
  if (dayNumbers.some((day) => WEEKEND_DAYS.includes(day)))
    return START_OF_WEEK_DAYS;
  return [];
};

const getSourcePlanDay = (sourcePlan) => {
  if (!sourcePlan) return null;
  if (
    sourcePlan.defaultActiveTemplate &&
    sourcePlan.day != null &&
    sourcePlan.day !== ""
  )
    return Number(sourcePlan.day);
  if (sourcePlan.activeDate) return moment.utc(sourcePlan.activeDate).day();
  return null;
};

const Copy = () => {
  const [selectedDays, setSelectedDays] = useState([]);
  const [copySettings, setCopySettings] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const activeSaebiVersion = useSelector(activeSaebiVersionSelector);
  const isDailyPlan = useSelector(isDailyPlanSelector);
  const selectedProfile = useSelector(getSelectedMatrixProfile);
  const { dayProfiles, datedProfiles, templates } =
    useSelector(getMatrixProfiles);
  const hasDriverlessRoutes = useSelector(hasDriverlessRoutesSelector);
  const open = useSelector(copyModalOpenSelector);
  const dispatch = useDispatch();

  const sourcePlan = findProfile({
    selectedProfile,
    dayProfiles,
    datedProfiles,
    templates,
  });
  const sourceDay = getSourcePlanDay(sourcePlan);
  const disabledDays = [
    ...new Set([
      ...(isDailyPlan ? [Number(selectedProfile.value)] : []),
      ...peerDaysToBlock(sourceDay == null ? [] : [sourceDay]),
      ...peerDaysToBlock(selectedDays.map((day) => day.value)),
    ]),
  ];

  const incompatibleDays = dayProfiles
    .filter(({ saebiVersion }) => saebiVersion !== activeSaebiVersion)
    .map(({ day }) => +day);

  const copyDisabled = !selectedDays.length || !copySettings;
  const copyDrivers = copySettings === COPY_PLAN_WITH_DRIVERS;

  const getConfirmMessage = () => {
    if (!copyDrivers)
      return `Are you sure you want to copy Route Profiles without their Drivers? Route Mapping will attempt to re-assign current Driver Profiles but some Drivers might become invalid for their new Route and as such will have to be taken off.`;
    else
      return `Are you sure? ${
        hasDriverlessRoutes
          ? "You still have routes with unallocated drivers on a currently selected plan. "
          : ""
      }If you continue this will override all Driver Profiles in Depot Planning for the selected days.`;
  };

  const onDaySelect = (item) => {
    const index = selectedDays.findIndex((day) => day.value === item.value);
    if (index < 0) {
      setSelectedDays(selectedDays.concat(item));
    } else {
      const selected = [...selectedDays];
      selected.splice(index, 1);
      setSelectedDays(selected);
    }
  };

  const onClose = () => {
    setSelectedDays([]);
    setCopySettings(null);
    setShowConfirmModal(false);
    dispatch(setCopyModalOpen(false));
  };

  const onCopy = () => {
    setSelectedDays([]);
    setCopySettings(null);
    setShowConfirmModal(false);
    dispatch(
      copyPlan({
        selectedDays,
        copyDrivers,
      })
    );
  };

  return (
    <Modal
      open={open}
      title={"Copy To"}
      onClose={onClose}
      content={
        <>
          <CopyContent
            selectedDays={selectedDays.map((i) => i.value)}
            onDaySelect={onDaySelect}
            disabledDays={disabledDays}
            incompatibleDays={incompatibleDays}
            copySettings={copySettings}
            onCopySettingsChange={setCopySettings}
          />
          <ConfirmDialog
            open={!!showConfirmModal}
            onDecline={() => setShowConfirmModal(false)}
            onConfirm={onCopy}
          >
            <Grid container>
              <Grid item xs={2}>
                <WarningIcon />
              </Grid>
              <Grid item xs={10}>
                {getConfirmMessage()}
              </Grid>
            </Grid>
          </ConfirmDialog>
        </>
      }
      actions={
        <CopyActions
          copyDisabled={copyDisabled}
          onCopy={() => setShowConfirmModal(true)}
        />
      }
    />
  );
};

export default Copy;
